import React, { useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Send, Trash2, Activity, UserPlus, UserMinus, CheckCircle2,
  ArrowRight, Circle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { relativeTime, absoluteTime } from "../lib/utils";
import { subMeta } from "../lib/pipeline";

// Filter tabs — each maps to the set of event.kind values it should keep.
// "all" is a passthrough. Ordering matters (renders in tab order).
const FILTER_TABS = [
  { key: "all",         label: "All",         kinds: null },
  { key: "stage",       label: "Stage moves", kinds: new Set(["stage_change", "item_stage_change"]) },
  { key: "comment",     label: "Notes",       kinds: new Set(["comment"]) },
  { key: "assignment",  label: "Assignments", kinds: new Set(["assignment_added", "assignment_removed"]) },
];

/**
 * Shopify-style unified job timeline. Merges auto-events (status_history
 * — stage moves, assignment adds/removes, per-item stage moves) with
 * manual staff comments (activity_notes) into one chronological feed.
 *
 * Data sources on the job document:
 *   status_history[] — auto events pushed by pipeline.py / server.py
 *   activity_notes[] — free-form staff comments
 *
 * Backend endpoints used:
 *   POST   /api/jobs/{id}/activity-notes
 *   DELETE /api/jobs/{id}/activity-notes/{note_id}
 */
export default function JobTimeline({ job, canEdit = true, onChange }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const events = mergeEvents(job);

  // Client-side filter — cheap, and lets us keep counts next to each tab so
  // the user sees "Notes (0)" instead of clicking through to an empty list.
  const filteredEvents = useMemo(() => {
    const active = FILTER_TABS.find((t) => t.key === filter);
    if (!active || !active.kinds) return events;
    return events.filter((ev) => active.kinds.has(ev.kind));
  }, [events, filter]);

  const tabCounts = useMemo(() => {
    const counts = { all: events.length };
    for (const t of FILTER_TABS) {
      if (!t.kinds) continue;
      counts[t.key] = events.filter((ev) => t.kinds.has(ev.kind)).length;
    }
    return counts;
  }, [events]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/activity-notes`, { text: trimmed });
      onChange?.({ ...job, activity_notes: [...(job.activity_notes || []), data] });
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/jobs/${job.id}/activity-notes/${noteId}`);
      onChange?.({ ...job, activity_notes: (job.activity_notes || []).filter((n) => n.id !== noteId) });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-3" data-testid={`job-timeline-${job.id}`}>
      <div className="flex items-center gap-2 text-label">
        <Activity className="w-3 h-3" /> Timeline
        <span className="text-slate-500 normal-case text-[10px] tracking-normal">
          ({events.length} {events.length === 1 ? "entry" : "entries"})
        </span>
      </div>

      {/* Filter tabs. Hidden when the feed is short enough that filtering
          is pointless (< 4 entries) so we don't add UI chrome for no gain. */}
      {events.length >= 4 && (
        <div className="flex items-center gap-1 flex-wrap" data-testid={`job-timeline-filter-${job.id}`}>
          {FILTER_TABS.map((t) => {
            const count = tabCounts[t.key] || 0;
            const active = filter === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`job-timeline-filter-${t.key}-${job.id}`}
                onClick={() => setFilter(t.key)}
                className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border transition ${
                  active
                    ? "bg-white text-black border-white font-semibold"
                    : "bg-white/[0.02] border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                }`}
              >
                {t.label}
                <span className={`ml-1.5 ${active ? "text-black/60" : "text-slate-500"}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
      )}

      {canEdit && (
        <form onSubmit={submit} className="flex gap-2" data-testid={`job-timeline-form-${job.id}`}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Leave a comment — e.g. 'Customer confirmed black stripe delete'"
            className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
            data-testid={`job-timeline-input-${job.id}`}
          />
          <button
            type="submit"
            disabled={busy || !text.trim()}
            data-testid={`job-timeline-submit-${job.id}`}
            className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-40 text-white flex items-center gap-1.5 text-xs"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      )}

      {events.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-1 py-2">
          No timeline entries yet. Stage moves and staff comments will appear here.
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-1 py-2" data-testid={`job-timeline-empty-filter-${job.id}`}>
          No entries in this view. Try a different filter above.
        </div>
      ) : (
        <ol className="relative border-l border-white/10 ml-2 space-y-3">
          {filteredEvents.map((ev, i) => (
            <TimelineRow key={ev._key || i} event={ev} onDelete={remove} currentUser={user} />
          ))}
        </ol>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function TimelineRow({ event, onDelete, currentUser }) {
  const { icon: Icon, color, title, subtitle } = renderEvent(event);
  const canDelete =
    event.kind === "comment" &&
    (currentUser?.role === "admin" ||
      currentUser?.role === "office" ||
      event.by_user_id === currentUser?.id);
  return (
    <li className="relative pl-6" data-testid={`timeline-row-${event.kind}`}>
      <span
        className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full flex items-center justify-center border border-white/10 ${color}`}
      >
        <Icon className="w-2.5 h-2.5" />
      </span>
      <div className="text-sm text-slate-100 leading-snug">{title}</div>
      {subtitle && <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>}
      <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-widest text-slate-500">
        <span title={absoluteTime(event.at)}>{relativeTime(event.at)}</span>
        {event.by_user_name && (
          <>
            <span>·</span>
            <span>by {event.by_user_name}</span>
          </>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => onDelete(event.id)}
            data-testid={`timeline-delete-${event.id}`}
            className="ml-auto text-slate-500 hover:text-red-400 flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Event mapping                                                              */
/* -------------------------------------------------------------------------- */

function mergeEvents(job) {
  const out = [];
  // Auto events — pipeline stage moves, assignment adds/removes, per-item
  // moves. status_history is already an append-log, we just tag each row
  // with its kind so renderEvent knows how to label it.
  for (const [i, h] of (job.status_history || []).entries()) {
    const kind = h.kind
      || (h.item_id ? "item_stage_change" : "stage_change");
    out.push({
      ...h,
      kind,
      main_status: h.main_status || job.main_status,
      _key: `hist-${h.at || i}-${i}`,
    });
  }
  // Manual comments
  for (const [i, n] of (job.activity_notes || []).entries()) {
    out.push({
      kind: "comment",
      id: n.id,
      text: n.text,
      at: n.created_at,
      by_user_id: n.by_user_id,
      by_user_name: n.by_user_name,
      _key: `note-${n.id || i}`,
    });
  }
  // Newest first (Shopify shows newest at top).
  out.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return out;
}

function renderEvent(event) {
  switch (event.kind) {
    case "comment":
      return {
        icon: MessageSquare,
        color: "bg-sky-500/20 text-sky-300",
        title: event.text,
        subtitle: null,
      };
    case "assignment_added":
      return {
        icon: UserPlus,
        color: "bg-emerald-500/20 text-emerald-300",
        title: (
          <>
            <b>{event.assignee_name || "Someone"}</b> added as {event.role}
          </>
        ),
        subtitle: null,
      };
    case "assignment_removed":
      return {
        icon: UserMinus,
        color: "bg-rose-500/20 text-rose-300",
        title: (
          <>
            <b>{event.assignee_name || "Someone"}</b> removed as {event.role}
          </>
        ),
        subtitle: null,
      };
    case "item_stage_change": {
      const meta = subMeta(event.main_status, event.sub_status);
      return {
        icon: CheckCircle2,
        color: "bg-amber-500/20 text-amber-300",
        title: (
          <>
            Item <b>{event.item_ref || "—"}</b> moved to <b>{meta.label}</b>
          </>
        ),
        subtitle: null,
      };
    }
    case "stage_change":
    default: {
      const meta = subMeta(event.main_status, event.sub_status);
      return {
        icon: ArrowRight,
        color: "bg-white/10 text-white",
        title: (
          <>
            Status changed to <b>{meta.label}</b>
          </>
        ),
        subtitle: null,
      };
    }
  }
}
